import React, { CSSProperties, Fragment, FunctionComponent } from "react";
import { connect, MapStateToProps } from "react-redux";

import { ButtonBackgroundColor, FlagCircle, IconButton, IconColor, IconSize, Layout, LocalizationProvider, Skeleton, SpacingSize, Text, WhiteSpace } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { bemClassNames } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { FlagColor, IAppState } from "@multiweb/types/react";
import { ParticipantType } from "../../../enums";
import { LOC_ADD, LOC_OWNER, LOC_REMOVE, LOC_SYSTEM } from "../../../locKeys";
import { isWorkflowReadOnly } from "../../../selectors";
import { IParticipant, WorkflowEditorProps } from "../../../types";

type AddableListFrameItemComponentProps = {
    readonly style: CSSProperties;
    readonly showAdd: boolean;
    readonly participant: IParticipant;
    readonly isWorkflowReadOnly: boolean;
    readonly onActionButtonClick: () => void;
    readonly systemText: string;
    readonly ownerText: string;
    readonly addText: string;
    readonly removeText: string;
};

const classNames = bemClassNames("addable-list-frame-item-component");

const AddableListFrameItemComponent: FunctionComponent<AddableListFrameItemComponentProps> = ({
    style,
    isWorkflowReadOnly,
    showAdd,
    participant,
    onActionButtonClick,
    systemText,
    ownerText,
    addText,
    removeText,
}) => {
    const isLoading = participant.name === undefined;
    const { name, color } = getParticipantNameAndColor(participant, ownerText, systemText);

    return (
        <div style={style}>
            <div className={classNames({ "is-loading": isLoading })}>
                {isLoading ? (
                    <Skeleton />
                ) : (
                    <Fragment>
                        {!isWorkflowReadOnly &&
                            <IconButton
                                size={IconSize.small}
                                title={showAdd ? addText : removeText}
                                onClick={onActionButtonClick}
                                color={showAdd ? IconColor.green : IconColor.red}
                                iconCode={showAdd ? IconCode.uiPlusSmall : IconCode.uiMinusSmall}
                                backgroundColor={ButtonBackgroundColor.transparent} />
                        }
                        <Layout spacingSizeLeft={SpacingSize.xs}>
                            <LocalizationProvider locKey={participant.locKey}>
                                {({ text }) => <FlagCircle title={text} {...{ color }} />}
                            </LocalizationProvider>
                        </Layout>
                        <Text value={name} title={name} ellipsis whiteSpace={WhiteSpace.nowrap} />
                    </Fragment>
                )}
            </div>
        </div>
    );
};

type StateProps = Pick<AddableListFrameItemComponentProps, "isWorkflowReadOnly" | "systemText" | "ownerText" | "addText" | "removeText">;

const mapStateToProps: MapStateToProps<StateProps, WorkflowEditorProps, IAppState> = (state, { hostKey }) => ({
    isWorkflowReadOnly: isWorkflowReadOnly(state, hostKey),
    systemText: getPrimaryText(state, LOC_SYSTEM) ?? "Systém",
    ownerText: getPrimaryText(state, LOC_OWNER) ?? "Vlastník",
    addText: getPrimaryText(state, LOC_ADD) ?? "Přidat",
    removeText: getPrimaryText(state, LOC_REMOVE) ?? "Odebrat",
});

export const AddableListFrameItem = connect(mapStateToProps)(AddableListFrameItemComponent);

const getParticipantNameAndColor = (participant: IParticipant, ownerText: string, systemText: string): { name: string; color: FlagColor } => {
    if (participant.participantType === undefined)
        return { name: "", color: "black" };
    if (participant.participantType === ParticipantType.User)
        return { name: participant.name, color: "blue" };
    if (participant.participantType === ParticipantType.Role)
        return { name: participant.name, color: "red" };
    if (participant.participantType === ParticipantType.DefinitionParticipant)
        return { name: participant.name, color: "pink" };
    if (participant.participantType === ParticipantType.Owner)
        return { name: ownerText, color: "green" };
    if (participant.participantType === ParticipantType.System)
        return { name: systemText, color: "black" };

    throw new Error(`Participant type '${participant.participantType}' is not implemented!`);
};
